import {ChangeDetectorRef, Component} from '@angular/core';

import * as moment from 'moment';
import {EMPTY} from 'rxjs';
import {catchError, finalize, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {PayPeriodAttributes} from '../../../models/models.interfaces';
import {Data} from '../../../shared/api/server-responses.interfaces';
import {Enums} from '../../../shared/utils/enums';
import {
  PayrollInputDataDTO,
  PayrollInputDTO,
  PersonModelMetadataDTO,
  SdcLogDTO,
  SdcLogStatuses
} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

@Component({
  selector: 'gp-sdc-payroll-form',
  templateUrl: './sdc-payroll-form.component.html',
  styleUrls: ['./sdc-payroll-form.component.scss']
})
export class SdcPayrollFormComponent extends Destroyable {

  public readonly statuses: string[] = Enums.getNames(SdcLogStatuses);
  public readonly payrollVariableTypes = ['OneOffPayrollInputData', 'RecurringPayrollInputData'];

  private readonly PAYROLL_VARIABLE_LABELS: { [variableType: string]: string } = {
    OneOffPayrollInputData: 'One-off',
    RecurringPayrollInputData: 'Recurring'
  };

  public payroll: PayrollInputDTO;

  public loading = false;
  public errorPanelOpened = false;

  public editedPayPeriod: Data<PayPeriodAttributes>;

  public contractId: string;
  public contractBeginDate: string;
  public contractEndDate: string;

  constructor(private sdconnectService: SdconnectService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public metadata(variableType: string): PersonModelMetadataDTO {
    const metadata = this.payroll.metadata.find(m => m.name === variableType);
    return metadata && {...metadata, name: this.PAYROLL_VARIABLE_LABELS[variableType] ?? metadata.name};
  }

  public data(variableType: string): PayrollInputDataDTO[] {
    const items = this.payroll?.data[variableType];
    if (!items || !this.editedPayPeriod || !this.dated(variableType)) {
      return items;
    }
    return items.filter(item => this.overlapsEditedPayPeriod(item));
  }

  public fields(variableType: string): string[] {
    const metadata = this.metadata(variableType);
    return metadata ? SdconnectService.standardFields(metadata) : [];
  }

  public additionalFields(variableType: string): string[] {
    const metadata = this.metadata(variableType);
    return metadata ? SdconnectService.additionalFields(metadata, this.data(variableType) || []) : [];
  }

  public dated(variableType: string): boolean {
    const metadata = this.metadata(variableType);
    return !!metadata && SdconnectService.entityIsDated(metadata);
  }

  public load(contractId: string, contractBeginDate: string, contractEndDate: string) {
    if (this.contractId !== contractId) {
      this.editedPayPeriod = undefined;
    }
    this.contractId = contractId;
    this.contractBeginDate = contractBeginDate;
    this.contractEndDate = contractEndDate;
    this.payroll = undefined;
    this.loading = true;
    this.sdconnectService.getPayroll(contractId)
      .pipe(
        tap((payroll) => this.payroll = payroll),
        catchError(() => EMPTY),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public hasErrors(): boolean {
    return this.failedSdcLogs().length > 0;
  }

  public failedSdcLogs(): SdcLogDTO[] {
    return this.payroll?.sdcLogs?.filter(sdcLog => sdcLog.status === SdcLogStatuses.FAILED) || [];
  }

  public failedPayrollVariableTypes(): string[] {
    return Object.entries(this.payroll?.data || {})
      .filter(([, items]) =>
        (items || []).some(item => item.status === SdcLogStatuses.FAILED))
      .map(([variableType]) => variableType);
  }

  public openErrorPanel(): void {
    this.errorPanelOpened = true;
  }

  public onChange(sidePanelOpenChange: boolean): void {
    this.errorPanelOpened = sidePanelOpenChange;
  }

  public scrollTo(variableType: string): void {
    if (!variableType) {
      return;
    }
    const el = document.getElementById(`${variableType}-variableBlock`);
    el?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  public onPayPeriodChange(payPeriod: Data<PayPeriodAttributes>): void {
    this.editedPayPeriod = payPeriod;
  }

  private overlapsEditedPayPeriod(item: PayrollInputDataDTO): boolean {
    const startDate = item.values?.['startDate'];
    const endDate = item.values?.['endDate'];
    const {begindate, enddate} = this.editedPayPeriod.attributes;
    return (!startDate || moment(startDate).isSameOrBefore(enddate, 'day'))
      && (!endDate || moment(endDate).isSameOrAfter(begindate, 'day'));
  }
}
